import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Bus, Lock, Mail } from 'lucide-react'
import backgroundImage from '@assets/image.png'
import {
  Box,
  Container,
  VStack,
  Text,
  Heading,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Button,
  IconButton,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Spinner,
} from '@chakra-ui/react'
import { useAuthStore } from '@store/authStore'
import { getDeviceInfo } from '@utils/device'
import toast from 'react-hot-toast'

interface LoginForm {
  email: string
  password: string
}

export default function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>()

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      // Get device information
      const deviceInfo = getDeviceInfo()
      
      // Call the user-management service for authentication
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          deviceId: deviceInfo.deviceId,
          deviceType: deviceInfo.deviceType
        }),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        // Check if user has admin role
        if (result.data.user.role === 'admin' || result.data.user.role === 'operator') {
          login(result.data.tokens.accessToken)
          toast.success('Welcome to Daladala Smart!')
        } else {
          toast.error('Access denied. Admin or Owner role required.')
        }
      } else {
        toast.error(result.message || 'Invalid email or password')
      }
    } catch (error) {
      console.error('Login error:', error)
      toast.error('Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const cardBg = useColorModeValue('white', 'gray.800')

  return (
    <Box
      minH="100vh"
      backgroundImage={`url(${backgroundImage})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      display="flex"
      alignItems="center"
      justifyContent="center"
      py={12}
      px={{ base: 4, sm: 6, lg: 8 }}
      position="relative"
    >
      {/* Overlay for better readability */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.400"
        zIndex={1}
      />
      <Container maxW="md" position="relative" zIndex={2}>
        <VStack spacing={8}>
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <Box
              w="16"
              h="16"
              bgGradient="linear(to-br, brand.500, brand.600)"
              borderRadius="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              shadow="lg"
            >
              <Bus size={32} color="white" />
            </Box>
            <VStack spacing={2}>
              <Heading size="xl" color="white">
                Daladala Smart
              </Heading>
              <Text color="white" fontWeight="medium">
                CMS Portal
              </Text>
            </VStack>
          </VStack>

          {/* Login Form */}
          <Card bg={cardBg} shadow="xl" borderRadius="2xl" w="full">
            <CardBody p={8}>
              <VStack as="form" spacing={6} onSubmit={handleSubmit(onSubmit)}>
                {/* Email Field */}
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    Email Address
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Mail size={20} color="var(--chakra-colors-gray-400)" />
                    </InputLeftElement>
                    <Input
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      type="email"
                      placeholder="Enter your email"
                      size="lg"
                      borderRadius="xl"
                      borderColor="gray.300"
                      _focus={{
                        borderColor: 'brand.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                      }}
                    />
                  </InputGroup>
                  {errors.email && (
                    <Text fontSize="sm" color="red.600" mt={1}>
                      {errors.email.message}
                    </Text>
                  )}
                </FormControl>

                {/* Password Field */}
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    Password
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Lock size={20} color="var(--chakra-colors-gray-400)" />
                    </InputLeftElement>
                    <Input
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters',
                        },
                      })}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      size="lg"
                      borderRadius="xl"
                      borderColor="gray.300"
                      _focus={{
                        borderColor: 'brand.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                      }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        icon={showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        color="gray.400"
                        _hover={{ color: 'gray.600' }}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {errors.password && (
                    <Text fontSize="sm" color="red.600" mt={1}>
                      {errors.password.message}
                    </Text>
                  )}
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="submit"
                  isLoading={isLoading}
                  loadingText="Signing in..."
                  size="lg"
                  w="full"
                  colorScheme="brand"
                  bgGradient="linear(to-r, brand.500, brand.600)"
                  _hover={{
                    bgGradient: 'linear(to-r, brand.600, brand.700)',
                  }}
                  borderRadius="xl"
                  py={6}
                >
                  Sign In
                </Button>
              </VStack>

              {/* Admin Login Info */}
              <Alert status="info" mt={6} borderRadius="xl">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm" mb={1}>
                    Admin Access Required:
                  </AlertTitle>
                  <AlertDescription fontSize="xs">
                    <VStack align="start" spacing={0}>
                      <Text>Use your admin credentials to access the management portal.</Text>
                      <Text>Contact your system administrator if you need access.</Text>
                    </VStack>
                  </AlertDescription>
                </Box>
              </Alert>
            </CardBody>
          </Card>

          {/* Footer */}
          <Text fontSize="sm" color="gray.500" textAlign="center">
            © 2025 Daladala Smart. All rights reserved.
          </Text>
        </VStack>
      </Container>
    </Box>
  )
}
